import { setNestedObjectValues } from 'formik';
import { set } from 'lodash';
import * as yup from 'yup';
import {
  addSetupFee,
  FundDraft,
  FundDraftStepData,
  addFundPlanType,
} from '../../redux-store/types/funds';
import { updatedSetupFee } from './addPlan';
import {
  isApplicableSetupfee,
  isFieldApplicable,
  isFieldVariable,
} from '../../utils/utilityFunctions';

export const FUND_DRAFT_STEP_KEYS: Record<1 | 2 | 3, FundFormSection> = {
  1: 'basic',
  2: 'plan',
  3: 'investment',
};

export type FundFormSection = 'basic' | 'plan' | 'investment';

export const FUND_DRAFT_STEP_LABELS: Record<number, string> = {
  1: 'Basic Information',
  2: 'Plan & Structure',
  3: 'Codes & Identifiers',
};

export function normalizeFundDraftList(res: unknown): FundDraft[] {
  if (Array.isArray(res)) return res as FundDraft[];
  if (res && typeof res === 'object') {
    const body = res as { drafts?: FundDraft[]; data?: FundDraft[]; items?: FundDraft[] };
    if (Array.isArray(body.drafts)) return body.drafts;
    if (Array.isArray(body.data)) return body.data;
    if (Array.isArray(body.items)) return body.items;
  }
  return [];
}

export function extractFundDraft(res: unknown): FundDraft | null {
  if (!res || typeof res !== 'object') return null;
  const body = res as { draft?: FundDraft; data?: FundDraft; id?: string; step1Data?: unknown };
  if (body.draft && (body.draft.id || body.draft.step1Data)) return body.draft;
  if (body.data && (body.data.id || body.data.step1Data)) return body.data;
  if (body.id || body.step1Data) return body as FundDraft;
  return null;
}

function toBooleanOrNull(value: unknown): boolean | null {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
}

function toIdArray(ids: unknown): number[] {
  if (!Array.isArray(ids)) return [];
  return ids
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id))
    .sort((a, b) => a - b);
}

function toFeeNumberArray(values: addSetupFee[] | number[]): number[] {
  if (!Array.isArray(values) || values.length === 0) return [];
  if (typeof values[0] === 'number') {
    return (values as number[]).filter((fee) => fee !== null && fee !== undefined);
  }
  return (values as addSetupFee[])
    .filter((obj) => obj.isActive)
    .map((ele) => Number(ele.fee))
    .filter((fee) => !Number.isNaN(fee));
}

function toFeeRows(raw: unknown): updatedSetupFee[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [{ fee: null, sno: 0, isActive: true }];
  }
  return raw.map((item, i) => {
    if (item && typeof item === 'object' && 'fee' in (item as addSetupFee)) {
      const row = item as addSetupFee;
      return {
        fee: row.fee,
        sno: i,
        isActive: row.isActive !== false,
      };
    }
    return {
      fee: item as number,
      sno: i,
      isActive: true,
    };
  });
}

function formatSchemeStartDate(value: unknown): string {
  if (!value) return `${new Date()}`;
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

type WizardValues = addFundPlanType & { isApplicableForDistributor: boolean };

export function buildStep1Data(values: WizardValues): FundDraftStepData {
  const cvlEnable = toBooleanOrNull(values.CVL_ENABLE_NEW_API);
  return {
    schemeCode: values.schemeCode,
    schemeName: values.schemeName,
    amcCode: values.amcCode,
    bankName: values.bankName,
    bankBranch: values.bankBranch,
    ifscCode: values.ifscCode,
    bankAccountNumber: values.bankAccountNumber,
    LEEGALITY_USE_STAMP_SERIES: toBooleanOrNull(values.LEEGALITY_USE_STAMP_SERIES) ?? false,
    LEEGALITY_STAMP_SERIES: values.LEEGALITY_STAMP_SERIES,
    LEEGALITY_STAMP_SERIES_GROUP: values.LEEGALITY_STAMP_SERIES_GROUP,
    LEEGALITY_STAMP_VALUE: values.LEEGALITY_STAMP_VALUE,
    CVL_PWD: values.CVL_PWD,
    CVL_PASSCODE: values.CVL_PASSCODE,
    CVL_USERNAME: values.CVL_USERNAME,
    CVL_APP_POS_CODE: values.CVL_APP_POS_CODE,
    CVL_ENABLE_NEW_API: cvlEnable,
    CVL_JSON_AES_KEY: cvlEnable ? values.CVL_JSON_AES_KEY : null,
    CVL_JSON_API_KEY: cvlEnable ? values.CVL_JSON_API_KEY : null,
    indMinNetworth: values.indMinNetworth,
    nonIndMinNetworth: values.nonIndMinNetworth,
  };
}

export function buildStep2Data(values: WizardValues): FundDraftStepData {
  return {
    country: values.country,
    currency: values.currency,
    planCode: values.planCode,
    setupFee: values.setupFee ? values.setupFee : 0,
    custodian: values.custodian,
    faceValue: values.faceValue,
    trusteeIds: toIdArray(values.trusteeIds),
    fundAddress1: values.fundAddress1,
    fundAddress2: values.fundAddress2,
    fundAddress3: values.fundAddress3,
    lockInPeriod: values.lockInPeriod,
    schemeNature: values.schemeNature,
    fundmanagerIds: toIdArray(values.fundmanagerIds),
    schemeCategory: values.schemeCategory,
    lockInExtension: values.lockInExtension,
    planDescription: values.planDescription,
    schemeStartDate: formatSchemeStartDate(values.schemeStartDate),
    requiredTrustees: values.requiredTrustees != null ? Number(values.requiredTrustees) : null,
    registrationNumber: values.registrationNumber,
    requiredFundManagers:
      values.requiredFundManagers != null ? Number(values.requiredFundManagers) : null,
  };
}

export function buildStep3Data(values: WizardValues): FundDraftStepData {
  const setupFees = toFeeNumberArray(values.setupFeeValues);
  const equalisationPercents = toFeeNumberArray(values.equalisationPercentValues);
  return {
    tcLink: values.tcLink || '',
    placementFee: isFieldApplicable(values.placementFeeApplicable || '')
      ? values.placementFee
      : null,
    setupFeeValues: isApplicableSetupfee(values.setupFeeApplicable) ? setupFees : [],
    operatingExpenses: isFieldApplicable(values.operatingExpensesApplicable || '')
      ? values.operatingExpenses
      : null,
    setupFeeApplicable: values.setupFeeApplicable,
    equalisationAmtType: isFieldApplicable(values.equalisationAmtApplicable)
      ? values.equalisationAmtType
      : null,
    maxCommitmentAmount: values.maxCommitmentAmount,
    minCommitmentAmount: values.minCommitmentAmount,
    isApplicableForDirect: values.isApplicableForDirect,
    defaultEqualisationAmt: isFieldApplicable(values.equalisationAmtApplicable)
      ? values.defaultEqualisationAmt
      : 0,
    placementFeeApplicable: values.placementFeeApplicable,
    equalisationAmtApplicable: values.equalisationAmtApplicable,
    equalisationPercentValues: isApplicableSetupfee(values.equalisationPercentApplicable)
      ? equalisationPercents
      : [],
    minContributionPercentage: values.minContributionPercentage
      ? values.minContributionPercentage
      : 0,
    netCommitmentAmtApplicable: values.netCommitmentAmtApplicable,
    operatingExpensesApplicable: values.operatingExpensesApplicable,
    equalisationPercentApplicable: values.equalisationPercentApplicable,
    minEqualisationAmt: isFieldVariable(values.equalisationAmtType, values.equalisationAmtApplicable)
      ? values.minEqualisationAmt
      : null,
    maxEqualisationAmt: isFieldVariable(values.equalisationAmtType, values.equalisationAmtApplicable)
      ? values.maxEqualisationAmt
      : null,
  };
}

export function buildStepData(step: 1 | 2 | 3, values: WizardValues): FundDraftStepData {
  if (step === 1) return buildStep1Data(values);
  if (step === 2) return buildStep2Data(values);
  return buildStep3Data(values);
}

export function sectionToStep(section: FundFormSection): 1 | 2 | 3 {
  if (section === 'plan') return 2;
  if (section === 'investment') return 3;
  return 1;
}

export function getDraftSectionForErrors(errors: Record<string, unknown>): FundFormSection {
  const keys = Object.keys(errors || {});
  const step1Fields = [
    'schemeCode',
    'schemeName',
    'LEEGALITY_STAMP_SERIES',
    'LEEGALITY_STAMP_SERIES_GROUP',
    'LEEGALITY_STAMP_VALUE',
    'LEEGALITY_USE_STAMP_SERIES',
    'CVL_USERNAME',
    'CVL_PWD',
    'CVL_PASSCODE',
    'CVL_APP_POS_CODE',
    'CVL_ENABLE_NEW_API',
    'CVL_JSON_AES_KEY',
    'CVL_JSON_API_KEY',
    'indMinNetworth',
    'nonIndMinNetworth',
  ];
  const step2Fields = [
    'planCode',
    'planDescription',
    'schemeNature',
    'fundmanagerIds',
    'trusteeIds',
    'requiredFundManagers',
    'requiredTrustees',
    'schemeCategory',
  ];
  const hasField = (fields: string[]) =>
    keys.some((key) => fields.includes(key.split('.')[0].split('[')[0]));
  if (hasField(step1Fields)) return 'basic';
  if (hasField(step2Fields)) return 'plan';
  return 'investment';
}

export function mapDraftToForm(draft: FundDraft, base: WizardValues): WizardValues {
  const step1 = (draft.step1Data || {}) as Record<string, unknown>;
  const step2 = (draft.step2Data || {}) as Record<string, unknown>;
  const step3 = (draft.step3Data || {}) as Record<string, unknown>;

  return {
    ...base,
    ...step1,
    ...step2,
    ...step3,
    schemeStartDate: step2.schemeStartDate
      ? String(step2.schemeStartDate)
      : base.schemeStartDate,
    fundmanagerIds: Array.isArray(step2.fundmanagerIds)
      ? (step2.fundmanagerIds as (string | number)[]).map(String)
      : base.fundmanagerIds,
    trusteeIds: Array.isArray(step2.trusteeIds)
      ? (step2.trusteeIds as (string | number)[]).map(String)
      : base.trusteeIds,
    setupFeeValues: toFeeRows(step3.setupFeeValues),
    equalisationPercentValues: toFeeRows(step3.equalisationPercentValues),
    LEEGALITY_USE_STAMP_SERIES:
      toBooleanOrNull(step1.LEEGALITY_USE_STAMP_SERIES) ?? base.LEEGALITY_USE_STAMP_SERIES,
    CVL_ENABLE_NEW_API: toBooleanOrNull(step1.CVL_ENABLE_NEW_API),
  } as WizardValues;
}

export async function validateYupSchemaOnValues(
  schema: yup.AnyObjectSchema,
  values: unknown,
  setErrors: (errors: Record<string, unknown>) => void,
  setTouched: (touched: Record<string, unknown>) => void
): Promise<boolean> {
  try {
    await schema.validate(values, { abortEarly: false });
    return true;
  } catch (err) {
    if (err instanceof yup.ValidationError) {
      const errors: Record<string, unknown> = {};
      err.inner.forEach((e) => {
        if (e.path) {
          set(errors, e.path, e.message);
        }
      });
      setErrors(errors);
      setTouched(setNestedObjectValues(errors, true) as Record<string, unknown>);
    }
    return false;
  }
}
